import { ClipboardEvent, FunctionComponent, useRef } from "react";

import {
  FieldController,
  FontSizes,
  IMaskedTextField,
  Stack,
  TextField,
  useTheme
} from "@bps/fluent-ui";
import { useFormContext } from "@bps/form-api";

import {
  ARROW_LEFT_KEY,
  ARROW_RIGHT_KEY,
  BACKSPACE_KEY,
  dobPlaceholders,
  PIN_LENGTH,
  pinFields
} from "./VerifyPinForm.constants";
import { VerifyPinFormValues } from "./VerifyPinForm.interface";
import { next } from "./VerifyPinForm.utils";

interface VerifyPinFormInputsProps {
  isError: boolean;
}

export const VerifyPinFormInputs: FunctionComponent<
  VerifyPinFormInputsProps
> = () => {
  const theme = useTheme();
  const { actions } = useFormContext<VerifyPinFormValues>();
  const refs = useRef<Record<string, IMaskedTextField>>({});

  const onPaste =
    (isFirstInputElement: boolean) =>
    (evt: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (isFirstInputElement) {
        evt.preventDefault();
        const pasteContent: string = evt.clipboardData.getData("text");
        const valueToSet = pasteContent.substring(0, PIN_LENGTH);

        if (!!valueToSet) {
          const values = pinFields.reduce((acc, field, idx) => {
            return { ...acc, [field]: valueToSet[idx] };
          }, {});

          actions.setValues(values as VerifyPinFormValues);
        }
      }
    };

  return (
    <Stack horizontal tokens={{ childrenGap: 8 }}>
      {pinFields.map((n, idx) => {
        const isFirstInputElement = idx === 0;
        return (
          <FieldController name={n} key={n}>
            {({ value, setFieldValue, focusField, blurField }) => (
              <TextField
                value={value}
                onChange={(event, newValue) => {
                  event.stopPropagation();
                  event.preventDefault();
                  setFieldValue(newValue);
                }}
                onBlur={blurField}
                autoFocus={isFirstInputElement}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                componentRef={r => {
                  if (r && !refs.current[n]) {
                    refs.current[n] = r;
                  }
                }}
                placeholder={dobPlaceholders[idx]}
                onPaste={onPaste(isFirstInputElement)}
                name={n}
                onFocus={evt => {
                  evt.target.setSelectionRange(0, 1);
                  focusField();
                }}
                onKeyUp={evt => {
                  const shouldNotMoveCursor =
                    evt.key.length > 1 &&
                    evt.code !== ARROW_LEFT_KEY &&
                    evt.code !== ARROW_RIGHT_KEY &&
                    evt.code !== BACKSPACE_KEY;

                  if (shouldNotMoveCursor) return;

                  // Focus the next or previous input after a value has been
                  // entered or deleted respectively{
                  const nextField = next<IMaskedTextField>(
                    refs.current,
                    n,
                    evt.key === ARROW_LEFT_KEY || evt.code === BACKSPACE_KEY
                  );
                  if (nextField) {
                    nextField.focus();
                  }
                }}
                autoComplete="one-time-code"
                maxLength={1}
                styles={{
                  field: {
                    fontSize: FontSizes.xLargePlus,
                    lineHeight: 20,
                    textAlign: "center",
                    height: 54,
                    width: 48,
                    position: "relative"
                  },
                  fieldGroup: {
                    height: "auto",
                    borderColor: theme.palette.themePrimary,
                    "&:hover": { borderColor: theme.palette.themePrimary }
                  }
                }}
              />
            )}
          </FieldController>
        );
      })}
    </Stack>
  );
};
